import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Form, Modal } from 'react-bootstrap';
import {
  authorizedFetch,
  clientAuthorizedGet,
  clientAuthorizedPost,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';
import { API_ROUTES } from 'utils/constants';
import { StorageProvider } from 'types/storageProvider';
import {
  StorageFile,
  UploadUrlResponse,
  RegisterFileRequest,
  DownloadUrlResponse,
  DownloadAllUrlsResponse,
  FileWithDownloadUrl,
  DeleteFolderResponse,
  BatchDeleteFilesResponse,
} from 'types/storageFile';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import {
  HardDrive,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Download,
  FileText,
  Archive,
  ExternalLink,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderMinus,
  ChevronRight,
} from 'lucide-react';
import AddStorageProviderModal from './AddStorageProviderModal';
import EditStorageProviderModal from './EditStorageProviderModal';
import styles from './Storage.module.scss';

const EXPLORER_PAGE_LIMIT = 1000;

/** Normalize folder path: always end with / when non-empty, no leading slash */
function normalizeFolderPath(path: string): string {
  const trimmed = path.replace(/^\/+|\/+$/g, '').trim();
  return trimmed ? `${trimmed}/` : '';
}

/** From a list of file paths under a prefix, get immediate subfolder names and files in current folder */
function deriveExplorerContent(
  files: StorageFile[],
  currentPrefix: string,
): { subfolders: string[]; filesInFolder: StorageFile[] } {
  const subfolders = new Set<string>();
  const filesInFolder: StorageFile[] = [];
  const prefix = currentPrefix || '';
  for (const f of files) {
    if (!f.path.startsWith(prefix)) continue;
    const relative = f.path.slice(prefix.length);
    if (!relative) continue;
    const firstSlash = relative.indexOf('/');
    if (firstSlash >= 0) {
      subfolders.add(relative.slice(0, firstSlash));
    } else {
      filesInFolder.push(f);
    }
  }
  return {
    subfolders: Array.from(subfolders).sort(),
    filesInFolder: filesInFolder.sort((a, b) =>
      a.filename.localeCompare(b.filename),
    ),
  };
}

/** Breadcrumb segments from folder path (e.g. "invoices/2025/" -> ["invoices", "2025"]) */
function breadcrumbSegments(folderPath: string): string[] {
  const normalized = folderPath.replace(/\/+$/, '');
  return normalized ? normalized.split('/') : [];
}

function Storage() {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [provider, setProvider] = useState<StorageProvider | null>(null);
  const [loadingProvider, setLoadingProvider] = useState(true);
  const [reloadingProvider, setReloadingProvider] = useState(false);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [editingProviderId, setEditingProviderId] = useState<string | null>(
    null,
  );
  const [providerToDelete, setProviderToDelete] =
    useState<StorageProvider | null>(null);
  const [deletingProviderId, setDeletingProviderId] = useState<string | null>(
    null,
  );

  const [files, setFiles] = useState<StorageFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [reloadingFiles, setReloadingFiles] = useState(false);
  const [currentFolderPath, setCurrentFolderPath] = useState('');
  const [uploadSubfolder, setUploadSubfolder] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewingFile, setViewingFile] = useState<StorageFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<StorageFile | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const [deletingFolderPath, setDeletingFolderPath] = useState<string | null>(
    null,
  );
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);

  const [downloadAllPathPrefix, setDownloadAllPathPrefix] = useState('');
  const [downloadAllLimit, setDownloadAllLimit] = useState(1000);
  const [downloadAllOffset, setDownloadAllOffset] = useState(0);
  const [downloadAllExpires, setDownloadAllExpires] = useState(3600);
  const [downloadAllResult, setDownloadAllResult] = useState<
    FileWithDownloadUrl[] | null
  >(null);
  const [loadingDownloadAll, setLoadingDownloadAll] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingAllProgress, setDownloadingAllProgress] = useState<
    number | null
  >(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const loadProvider = useCallback(
    async (isReload = false) => {
      if (isReload) setReloadingProvider(true);
      else setLoadingProvider(true);
      try {
        const config = await clientAuthorizedGet<StorageProvider | null>(
          API_ROUTES.STORAGE_PROVIDERS,
          undefined,
          undefined,
        );
        setProvider(config);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : 'Failed to load storage provider.';
        showToast(msg, 'error');
        setProvider(null);
      } finally {
        if (isReload) setReloadingProvider(false);
        else setLoadingProvider(false);
      }
    },
    [showToast],
  );

  const loadFiles = useCallback(
    async (isReload = false, folderPath?: string) => {
      const prefix = normalizeFolderPath(folderPath ?? currentFolderPath);
      if (isReload) setReloadingFiles(true);
      else setLoadingFiles(true);
      try {
        const params: Record<string, string> = {
          limit: String(EXPLORER_PAGE_LIMIT),
          offset: '0',
        };
        if (prefix) params.pathPrefix = prefix;
        const list = await clientAuthorizedGet<StorageFile[]>(
          API_ROUTES.FILES,
          params,
          undefined,
        );
        setFiles(Array.isArray(list) ? list : []);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to load files.';
        showToast(msg, 'error');
        setFiles([]);
      } finally {
        if (isReload) setReloadingFiles(false);
        else setLoadingFiles(false);
      }
    },
    [currentFolderPath, showToast],
  );

  useEffect(() => {
    void loadProvider();
  }, [loadProvider]);

  useEffect(() => {
    if (provider) void loadFiles(false, currentFolderPath);
  }, [currentFolderPath, provider, loadFiles]);

  useEffect(() => {
    setSelectedFileIds(new Set());
  }, [currentFolderPath]);

  const toggleFileSelection = useCallback((id: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const navigateToFolder = useCallback((folderPath: string) => {
    const normalized = normalizeFolderPath(folderPath);
    setCurrentFolderPath(normalized);
  }, []);

  const handleCreateNewFolder = useCallback(() => {
    const name = newFolderName
      .trim()
      .replace(/\/+/g, '/')
      .replace(/^\/+|\/+$/g, '');
    if (!name) return;
    const newPath = `${currentFolderPath}${name}/`;
    navigateToFolder(newPath);
    setShowNewFolderModal(false);
    setNewFolderName('');
  }, [currentFolderPath, newFolderName, navigateToFolder]);

  const { subfolders, filesInFolder } = useMemo(
    () => deriveExplorerContent(files, currentFolderPath),
    [files, currentFolderPath],
  );
  const breadcrumbs = useMemo(
    () => breadcrumbSegments(currentFolderPath),
    [currentFolderPath],
  );

  const selectAllFilesInFolder = useCallback(() => {
    setSelectedFileIds((prev) => {
      const fileIds = filesInFolder.map((f) => f.id);
      const allSelected =
        fileIds.length > 0 && fileIds.every((id) => prev.has(id));
      return allSelected ? new Set<string>() : new Set(fileIds);
    });
  }, [filesInFolder]);

  const handleRefresh = useCallback(() => {
    void loadProvider(true);
    void loadFiles(true);
  }, [loadProvider, loadFiles]);

  const handleAddProviderSuccess = useCallback(
    (config: StorageProvider) => {
      setProvider(config);
      setShowAddProvider(false);
      showToast('Storage provider created.', 'success');
    },
    [showToast],
  );

  const handleEditProviderSuccess = useCallback(
    (config: StorageProvider) => {
      setProvider(config);
      setEditingProviderId(null);
      showToast('Storage provider updated.', 'success');
    },
    [showToast],
  );

  const handleDeleteProvider = useCallback(async () => {
    if (!providerToDelete) return;
    const id = providerToDelete.id;
    setDeletingProviderId(id);
    try {
      await clientAuthorizedDelete<{ ok?: boolean }>(
        `${API_ROUTES.STORAGE_PROVIDERS}/${id}`,
        undefined,
        undefined,
      );
      setProvider(null);
      setProviderToDelete(null);
      showToast('Storage provider removed.', 'success');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to delete provider.';
      showToast(msg, 'error');
    } finally {
      setDeletingProviderId(null);
    }
  }, [providerToDelete, showToast]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !provider) return;
      e.target.value = '';
      setUploading(true);
      const pathPrefix = uploadSubfolder.trim()
        ? `${currentFolderPath}${uploadSubfolder.trim().replace(/\/+$/, '')}/`
        : currentFolderPath || undefined;
      try {
        const uploadUrlRes = await clientAuthorizedPost<UploadUrlResponse>(
          API_ROUTES.FILES_UPLOAD_URL,
          {
            filename: file.name,
            pathPrefix: pathPrefix || undefined,
            contentType: file.type || undefined,
          } as Record<string, unknown>,
          undefined,
        );
        const { uploadUrl, path } = uploadUrlRes;
        const putRes = await authorizedFetch(
          uploadUrl,
          {
            method: 'PUT',
            body: file,
            headers: file.type ? { 'Content-Type': file.type } : {},
          },
          { skipAuth: true },
        );
        if (!putRes.ok) {
          throw new Error('Upload to storage failed.');
        }
        const registerBody: RegisterFileRequest = {
          path,
          filename: file.name,
          size: file.size,
          mimeType: file.type || 'application/octet-stream',
        };
        const registered = await clientAuthorizedPost<StorageFile>(
          API_ROUTES.FILES,
          registerBody as unknown as Record<string, unknown>,
          undefined,
        );
        setFiles((prev) => [registered, ...prev]);
        showToast(`"${file.name}" uploaded.`, 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed.';
        showToast(msg, 'error');
      } finally {
        setUploading(false);
      }
    },
    [provider, currentFolderPath, uploadSubfolder, showToast],
  );

  const handleDownload = useCallback(
    async (file: StorageFile) => {
      try {
        const res = await clientAuthorizedGet<DownloadUrlResponse>(
          `${API_ROUTES.FILES}/${file.id}/download-url`,
          undefined,
          undefined,
        );
        if (res.downloadUrl) {
          window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to get download link.';
        showToast(msg, 'error');
      }
    },
    [showToast],
  );

  const handleDeleteFile = useCallback(async () => {
    if (!fileToDelete) return;
    const id = fileToDelete.id;
    setDeletingFileId(id);
    try {
      await clientAuthorizedDelete<{ ok?: boolean }>(
        `${API_ROUTES.FILES}/${id}`,
        undefined,
        undefined,
      );
      setFiles((prev) => prev.filter((f) => f.id !== id));
      setFileToDelete(null);
      if (viewingFile?.id === id) setViewingFile(null);
      showToast('File deleted.', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete file.';
      showToast(msg, 'error');
    } finally {
      setDeletingFileId(null);
    }
  }, [fileToDelete, viewingFile, showToast]);

  const handleDeleteFolder = useCallback(async () => {
    const path = folderToDelete?.trim().replace(/\/+$/, '');
    if (path == null || path === '') return;
    setDeletingFolderPath(path);
    try {
      const res = await clientAuthorizedDelete<DeleteFolderResponse>(
        API_ROUTES.FILES,
        undefined,
        undefined,
        { pathPrefix: path },
      );
      const deleted = res?.deleted ?? 0;
      setFolderToDelete(null);
      const segments = path.split('/').filter(Boolean);
      const parentPath =
        segments.length > 1 ? segments.slice(0, -1).join('/') + '/' : '';
      navigateToFolder(parentPath);
      await loadFiles(false, parentPath);
      showToast(
        deleted > 0
          ? `Folder deleted. ${deleted} file(s) removed.`
          : 'Folder deleted.',
        'success',
      );
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to delete folder.';
      showToast(msg, 'error');
    } finally {
      setDeletingFolderPath(null);
    }
  }, [folderToDelete, navigateToFolder, loadFiles, showToast]);

  const BATCH_DELETE_CHUNK_SIZE = 100;

  const handleBatchDeleteFiles = useCallback(async () => {
    const ids = Array.from(selectedFileIds);
    if (ids.length === 0) return;
    setDeletingBatch(true);
    try {
      let totalDeleted = 0;
      for (let i = 0; i < ids.length; i += BATCH_DELETE_CHUNK_SIZE) {
        const chunk = ids.slice(i, i + BATCH_DELETE_CHUNK_SIZE);
        const res = await clientAuthorizedPost<BatchDeleteFilesResponse>(
          API_ROUTES.FILES_BATCH_DELETE,
          { ids: chunk },
        );
        totalDeleted += res?.deleted ?? 0;
      }
      setFiles((prev) => prev.filter((f) => !selectedFileIds.has(f.id)));
      setSelectedFileIds(new Set());
      setShowBatchDeleteConfirm(false);
      setViewingFile((v) => (v && selectedFileIds.has(v.id) ? null : v));
      showToast(
        totalDeleted > 0
          ? `${totalDeleted} file(s) deleted.`
          : 'No files were deleted.',
        'success',
      );
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to delete selected files.';
      showToast(msg, 'error');
    } finally {
      setDeletingBatch(false);
    }
  }, [selectedFileIds, showToast]);

  const loadFileDetails = useCallback(
    async (id: string) => {
      try {
        const file = await clientAuthorizedGet<StorageFile>(
          `${API_ROUTES.FILES}/${id}`,
          undefined,
          undefined,
        );
        setViewingFile(file);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to load file details.';
        showToast(msg, 'error');
      }
    },
    [showToast],
  );

  const handleGetDownloadAllUrls = useCallback(async () => {
    setLoadingDownloadAll(true);
    setDownloadAllResult(null);
    try {
      const params: Record<string, string> = {
        limit: String(Math.min(1000, Math.max(1, downloadAllLimit))),
        offset: String(Math.max(0, downloadAllOffset)),
        expiresInSeconds: String(
          Math.min(86400, Math.max(60, downloadAllExpires)),
        ),
      };
      if (downloadAllPathPrefix.trim())
        params.pathPrefix = downloadAllPathPrefix.trim();
      const res = await clientAuthorizedGet<DownloadAllUrlsResponse>(
        API_ROUTES.FILES_DOWNLOAD_ALL_URLS,
        params,
        undefined,
      );
      setDownloadAllResult(res?.files ?? []);
      showToast(`Got ${(res?.files ?? []).length} download URL(s).`, 'success');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to get download URLs.';
      showToast(msg, 'error');
    } finally {
      setLoadingDownloadAll(false);
    }
  }, [
    downloadAllPathPrefix,
    downloadAllLimit,
    downloadAllOffset,
    downloadAllExpires,
    showToast,
  ]);

  const triggerDirectDownload = useCallback(
    async (url: string, filename: string) => {
      const res = await authorizedFetch(
        url,
        { mode: 'cors' },
        { skipAuth: true },
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'download';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    },
    [],
  );

  const handleDirectDownloadOne = useCallback(
    async (f: FileWithDownloadUrl) => {
      try {
        await triggerDirectDownload(f.downloadUrl, f.filename);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Download failed.';
        showToast(msg, 'error');
      }
    },
    [triggerDirectDownload, showToast],
  );

  const handleDownloadAll = useCallback(async () => {
    if (!downloadAllResult || downloadAllResult.length === 0) return;
    setDownloadingAll(true);
    setDownloadingAllProgress(0);
    const total = downloadAllResult.length;
    let downloaded = 0;
    for (let i = 0; i < total; i++) {
      setDownloadingAllProgress(i + 1);
      const f = downloadAllResult[i];
      try {
        await triggerDirectDownload(f.downloadUrl, f.filename);
        downloaded += 1;
      } catch {
        showToast('A download failed. Stopped.', 'error');
        break;
      }
    }
    setDownloadingAll(false);
    setDownloadingAllProgress(null);
    if (downloaded > 0) {
      showToast(`Downloaded ${downloaded} file(s).`, 'success');
    }
  }, [downloadAllResult, triggerDirectDownload, showToast]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Storage</h1>
        <div className={styles.headerButtons}>
          <RefreshButton
            onClick={handleRefresh}
            loading={reloadingProvider || reloadingFiles}
          />
        </div>
      </div>

      {/* Storage provider section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <HardDrive
            size={20}
            style={{ verticalAlign: 'middle', marginRight: 8 }}
          />
          Storage provider
        </h2>
        {loadingProvider ? (
          <p className={styles.noProvider}>Loading…</p>
        ) : provider ? (
          <div className={styles.providerCard}>
            <div className={styles.providerInfo}>
              <span className={styles.providerBadge}>
                {provider.providerType}
              </span>
              <span className={styles.providerMeta}>
                Updated {new Date(provider.updatedAt).toLocaleString()}
              </span>
            </div>
            <div className={styles.providerActions}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingProviderId(provider.id)}
                aria-label="Edit storage provider"
              >
                <Pencil size={16} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setProviderToDelete(provider)}
                aria-label="Delete storage provider"
                className={styles.iconButton}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.noProvider}>
            <p>
              No storage provider configured. Add one to upload and manage
              files.
            </p>
            <Button onClick={() => setShowAddProvider(true)}>
              <Plus size={18} style={{ marginRight: 8 }} />
              Add storage provider
            </Button>
          </div>
        )}
      </section>

      {/* Files section - Explorer style */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <FileText
            size={20}
            style={{ verticalAlign: 'middle', marginRight: 8 }}
          />
          Files
        </h2>
        {!provider && (
          <p className={styles.emptyState}>
            Configure a storage provider above to list and upload files.
          </p>
        )}
        {provider && (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.breadcrumb}>
                <button
                  type="button"
                  className={styles.breadcrumbItem}
                  onClick={() => navigateToFolder('')}
                  aria-label="Root folder"
                >
                  <FolderOpen size={18} />
                  Home
                </button>
                {breadcrumbs.map((segment, i) => {
                  const pathUpToHere =
                    breadcrumbs.slice(0, i + 1).join('/') + '/';
                  return (
                    <React.Fragment key={pathUpToHere}>
                      <span className={styles.breadcrumbSep}>
                        <ChevronRight size={16} />
                      </span>
                      <button
                        type="button"
                        className={styles.breadcrumbItem}
                        onClick={() => navigateToFolder(pathUpToHere)}
                      >
                        {segment}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewFolderModal(true)}
                className={styles.newFolderButton}
                aria-label="Create new folder"
              >
                <FolderPlus size={18} style={{ marginRight: 6 }} />
                New folder
              </Button>
              {currentFolderPath && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setFolderToDelete(currentFolderPath)}
                  className={styles.deleteFolderButton}
                  aria-label="Delete this folder"
                >
                  <FolderMinus size={18} style={{ marginRight: 6 }} />
                  Delete this folder
                </Button>
              )}
            </div>
            <div
              className={styles.uploadZone}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              aria-label="Upload file"
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => void handleFileUpload(e)}
                disabled={uploading}
                aria-hidden
              />
              <Upload size={24} style={{ marginBottom: 8, opacity: 0.7 }} />
              <p className={styles.uploadZoneTitle}>
                {uploading ? 'Uploading…' : 'Upload to this folder'}
              </p>
              <p className={styles.uploadZonePath}>
                {currentFolderPath || '(root)'}
              </p>
              <input
                type="text"
                className={styles.pathPrefixInput}
                placeholder="Or type a subfolder name (e.g. reports)"
                value={uploadSubfolder}
                onChange={(e) => setUploadSubfolder(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: 280, marginTop: 8 }}
                aria-label="Upload to subfolder"
              />
            </div>
            <div className={styles.contentWrapper}>
              {reloadingFiles && (
                <div className={styles.loadingOverlay}>Loading…</div>
              )}
              {loadingFiles ? (
                <div className={styles.loadingState}>Loading…</div>
              ) : subfolders.length === 0 && filesInFolder.length === 0 ? (
                <div className={styles.emptyState}>
                  This folder is empty. Upload a file above or navigate to
                  another folder.
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  {selectedFileIds.size > 0 && (
                    <div className={styles.batchActionsBar}>
                      <span className={styles.batchActionsLabel}>
                        {selectedFileIds.size} file(s) selected
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSelectedFileIds(new Set())}
                        aria-label="Clear selection"
                      >
                        Clear selection
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setShowBatchDeleteConfirm(true)}
                        aria-label="Delete selected files"
                      >
                        <Trash2 size={16} style={{ marginRight: 6 }} />
                        Delete selected ({selectedFileIds.size})
                      </Button>
                    </div>
                  )}
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.checkboxColumn}>
                          {filesInFolder.length > 0 && (
                            <input
                              type="checkbox"
                              checked={
                                filesInFolder.length > 0 &&
                                filesInFolder.every((f) =>
                                  selectedFileIds.has(f.id),
                                )
                              }
                              ref={(el) => {
                                if (el)
                                  el.indeterminate =
                                    selectedFileIds.size > 0 &&
                                    selectedFileIds.size < filesInFolder.length;
                              }}
                              onChange={() => selectAllFilesInFolder()}
                              aria-label="Select all files in this folder"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                        <th>Name</th>
                        <th>Size</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subfolders.map((name) => {
                        const fullPath = `${currentFolderPath}${name}/`;
                        return (
                          <tr
                            key={fullPath}
                            className={styles.folderRow}
                            onClick={() => navigateToFolder(fullPath)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigateToFolder(fullPath);
                              }
                            }}
                          >
                            <td className={styles.checkboxColumn} />
                            <td className={styles.explorerNameCell}>
                              <Folder size={20} className={styles.folderIcon} />
                              <span className={styles.fileName}>{name}</span>
                            </td>
                            <td className={styles.sizeCell}>—</td>
                            <td>Folder</td>
                            <td />
                          </tr>
                        );
                      })}
                      {filesInFolder.map((f) => (
                        <tr
                          key={f.id}
                          className={styles.fileRow}
                          onClick={() => void loadFileDetails(f.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              void loadFileDetails(f.id);
                            }
                          }}
                          aria-label={`View details for ${f.filename}`}
                        >
                          <td className={styles.checkboxColumn}>
                            <input
                              type="checkbox"
                              checked={selectedFileIds.has(f.id)}
                              onChange={() => toggleFileSelection(f.id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${f.filename}`}
                            />
                          </td>
                          <td className={styles.explorerNameCell}>
                            <FileText
                              size={18}
                              className={styles.fileIcon}
                              aria-hidden
                            />
                            <span className={styles.fileName}>
                              {f.filename}
                            </span>
                          </td>
                          <td className={styles.sizeCell}>
                            {f.size >= 1024
                              ? `${(f.size / 1024).toFixed(1)} KB`
                              : `${f.size} B`}
                          </td>
                          <td>{f.mimeType || '—'}</td>
                          <td className={styles.actionsCell}>
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDownload(f);
                              }}
                              aria-label={`Download ${f.filename}`}
                              title="Download"
                            >
                              <Download size={16} />
                            </button>
                            <button
                              type="button"
                              className={`${styles.iconButton} ${styles.danger}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFileToDelete(f);
                              }}
                              aria-label={`Delete ${f.filename}`}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Backup / Get download URLs */}
      {provider && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <Archive
              size={20}
              style={{ verticalAlign: 'middle', marginRight: 8 }}
            />
            Backup / Get download URLs
          </h2>
          <p className={styles.noProvider}>
            Get signed download URLs for many files (e.g. for backup or
            migration). Use path prefix and pagination to scope results.
          </p>
          <div className={styles.filters}>
            <input
              type="text"
              className={styles.pathPrefixInput}
              placeholder="Path prefix (optional, e.g. invoices/2025/)"
              value={downloadAllPathPrefix}
              onChange={(e) => setDownloadAllPathPrefix(e.target.value)}
              aria-label="Path prefix for download URLs"
            />
            <label className={styles.downloadAllLabel}>
              <span>Limit</span>
              <input
                type="number"
                min={1}
                max={1000}
                value={downloadAllLimit}
                onChange={(e) =>
                  setDownloadAllLimit(Number(e.target.value) || 1000)
                }
                className={styles.downloadAllNumber}
                aria-label="Limit"
              />
            </label>
            <label className={styles.downloadAllLabel}>
              <span>Offset</span>
              <input
                type="number"
                min={0}
                value={downloadAllOffset}
                onChange={(e) =>
                  setDownloadAllOffset(Math.max(0, Number(e.target.value) || 0))
                }
                className={styles.downloadAllNumber}
                aria-label="Offset"
              />
            </label>
            <label className={styles.downloadAllLabel}>
              <span>URL expiry (s)</span>
              <input
                type="number"
                min={60}
                max={86400}
                value={downloadAllExpires}
                onChange={(e) =>
                  setDownloadAllExpires(
                    Math.min(
                      86400,
                      Math.max(60, Number(e.target.value) || 3600),
                    ),
                  )
                }
                className={styles.downloadAllNumber}
                aria-label="Expires in seconds"
              />
            </label>
            <Button
              variant="outline"
              onClick={() => void handleGetDownloadAllUrls()}
              disabled={loadingDownloadAll}
            >
              {loadingDownloadAll ? 'Loading…' : 'Get download URLs'}
            </Button>
          </div>
          {downloadAllResult !== null && (
            <div className={styles.tableContainer}>
              <div className={styles.downloadAllHeader}>
                <p className={styles.downloadAllCount}>
                  {downloadAllResult.length} file(s) with download URLs
                </p>
                {downloadAllResult.length > 0 && (
                  <Button
                    onClick={() => void handleDownloadAll()}
                    disabled={downloadingAll}
                    className={styles.downloadAllButton}
                  >
                    <Download size={18} style={{ marginRight: 8 }} />
                    {downloadingAll && downloadingAllProgress !== null
                      ? `Downloading ${downloadingAllProgress}/${downloadAllResult.length}…`
                      : 'Download all'}
                  </Button>
                )}
              </div>
              {downloadAllResult.length === 0 ? (
                <p className={styles.emptyState}>No files matched.</p>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Path</th>
                      <th>Size</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {downloadAllResult.map((f) => (
                      <tr key={f.id}>
                        <td className={styles.fileName}>{f.filename}</td>
                        <td>{f.path}</td>
                        <td className={styles.sizeCell}>
                          {f.size >= 1024
                            ? `${(f.size / 1024).toFixed(1)} KB`
                            : `${f.size} B`}
                        </td>
                        <td>
                          <div className={styles.downloadAllActions}>
                            <a
                              href={f.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.openInNewTabBtn}
                              title="Open in new tab (view in browser)"
                            >
                              <ExternalLink size={14} />
                              Open
                            </a>
                            <button
                              type="button"
                              className={styles.directDownloadBtn}
                              onClick={() => void handleDirectDownloadOne(f)}
                              title="Download file directly"
                            >
                              <Download size={14} />
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>
      )}

      <AddStorageProviderModal
        show={showAddProvider}
        onHide={() => setShowAddProvider(false)}
        onSuccess={handleAddProviderSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />
      {editingProviderId && (
        <EditStorageProviderModal
          show={!!editingProviderId}
          onHide={() => setEditingProviderId(null)}
          onSuccess={handleEditProviderSuccess}
          onError={(msg) => showToast(msg, 'error')}
          providerId={editingProviderId}
        />
      )}

      <Modal
        show={!!providerToDelete}
        onHide={() => setProviderToDelete(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove storage provider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Remove this storage provider? You will need to add one again to
            upload files. Existing file metadata may become invalid.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setProviderToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleDeleteProvider()}
            disabled={!!deletingProviderId}
          >
            {deletingProviderId ? 'Removing…' : 'Remove'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!viewingFile} onHide={() => setViewingFile(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>File details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingFile && (
            <dl style={{ margin: 0 }}>
              <dt>Filename</dt>
              <dd>{viewingFile.filename}</dd>
              <dt>Path</dt>
              <dd>{viewingFile.path}</dd>
              <dt>Size</dt>
              <dd>{viewingFile.size} bytes</dd>
              <dt>MIME type</dt>
              <dd>{viewingFile.mimeType || '—'}</dd>
              <dt>Created</dt>
              <dd>{new Date(viewingFile.createdAt).toLocaleString()}</dd>
              <dt>Updated</dt>
              <dd>{new Date(viewingFile.updatedAt).toLocaleString()}</dd>
            </dl>
          )}
        </Modal.Body>
        <Modal.Footer>
          {viewingFile && (
            <Button
              variant="outline"
              onClick={() => void handleDownload(viewingFile)}
            >
              Download
            </Button>
          )}
          <Button variant="secondary" onClick={() => setViewingFile(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!fileToDelete}
        onHide={() => setFileToDelete(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete file</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fileToDelete && (
            <p>
              Delete &quot;{fileToDelete.filename}&quot;? This cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFileToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleDeleteFile()}
            disabled={!!deletingFileId}
          >
            {deletingFileId ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showBatchDeleteConfirm}
        onHide={() => !deletingBatch && setShowBatchDeleteConfirm(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete selected files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete {selectedFileIds.size} selected file(s)? This cannot be
            undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBatchDeleteConfirm(false)}
            disabled={deletingBatch}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleBatchDeleteFiles()}
            disabled={deletingBatch}
          >
            {deletingBatch ? 'Deleting…' : 'Delete selected'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showNewFolderModal}
        onHide={() => {
          setShowNewFolderModal(false);
          setNewFolderName('');
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>New folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-0">
            <Form.Label>Folder name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. reports, 2025"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateNewFolder();
                }
              }}
              aria-label="Folder name"
            />
            <Form.Text className="text-muted">
              You will be taken to this folder (it can be empty). Upload files
              there to create it.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowNewFolderModal(false);
              setNewFolderName('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => handleCreateNewFolder()}
            disabled={!newFolderName.trim()}
          >
            Create folder
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={folderToDelete !== null}
        onHide={() => !deletingFolderPath && setFolderToDelete(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {folderToDelete && (
            <p>
              Delete folder &quot;{folderToDelete.replace(/\/+$/, '') || '/'}
              &quot;? All files in this folder (and any subfolders) will be
              permanently removed. This cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setFolderToDelete(null)}
            disabled={!!deletingFolderPath}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleDeleteFolder()}
            disabled={!!deletingFolderPath}
          >
            {deletingFolderPath ? 'Deleting…' : 'Delete folder'}
          </Button>
        </Modal.Footer>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Storage;
